import { BookOpen, FileText, ListChecks, StickyNote } from 'lucide-react';
import type { AgentCitation } from '../../lib/agentStream';
import type { ChatViewTurn } from '../../hooks/useChatSession';
import { citationLabel, dedupeCitations } from '../../lib/citations';
import { Markdown } from '../Markdown';
import { ApprovalCard } from './ApprovalCard';

interface ChatTranscriptProps {
  turns: ChatViewTurn[];
  /** Streamed model text for the in-flight turn (rendered live at the tail). */
  streamed: string;
  /** Status line under the tail (spinner text while running). */
  statusLabel: string;
  busy: boolean;
  /** Wired to the trailing pending proposal card only. */
  onRespond?: (approve: boolean) => void;
  deciding?: boolean;
}

function citationIcon(type: AgentCitation['type']) {
  switch (type) {
    case 'journal':
      return BookOpen;
    case 'list':
      return ListChecks;
    case 'note':
      return StickyNote;
    default:
      return FileText;
  }
}

/**
 * The conversation transcript: user rows, assistant Markdown (+ Sources),
 * proposal turns as ApprovalCards, then the live tail (streaming text +
 * status line). Pure rendering — all state lives in useChatSession.
 */
export function ChatTranscript({
  turns,
  streamed,
  statusLabel,
  busy,
  onRespond,
  deciding,
}: ChatTranscriptProps) {
  const lastIndex = turns.length - 1;

  return (
    <div className="space-y-4">
      {turns.map((turn, index) => {
        if (turn.role === 'user') {
          return (
            <div key={index} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] px-4 py-2.5 text-white">
                <p className="text-sm whitespace-pre-wrap break-words">{turn.text}</p>
              </div>
            </div>
          );
        }
        if (turn.role === 'assistant') {
          return (
            <div key={index} className="max-w-full">
              <Markdown content={turn.text} />
              <SourcesBlock citations={turn.citations} />
            </div>
          );
        }
        // Proposal turn: optional preamble + the approval card. Only the
        // TRAILING unresolved card gets the Approve/Decline handlers.
        const isPendingTail = index === lastIndex && !turn.resolution;
        return (
          <div key={index} className="space-y-2">
            {turn.text && <Markdown content={turn.text} />}
            <ApprovalCard
              proposals={turn.proposals}
              resolution={turn.resolution}
              onRespond={isPendingTail ? onRespond : undefined}
              deciding={deciding}
            />
          </div>
        );
      })}

      {streamed && <Markdown content={streamed} />}

      {(busy || statusLabel) && (
        <p role="status" className="flex items-center gap-2 text-sm text-[var(--text-tertiary)]">
          {busy && (
            <span
              aria-hidden
              className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)]"
            />
          )}
          {statusLabel}
        </p>
      )}
    </div>
  );
}

/** "Sources" block: non-interactive citation rows; omitted when empty. */
function SourcesBlock({ citations }: { citations: AgentCitation[] }) {
  const unique = dedupeCitations(citations);
  if (unique.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border)]">
      <h2 className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Sources</h2>
      <ul className="mt-2 space-y-2 list-none">
        {unique.map((citation) => {
          const Icon = citationIcon(citation.type);
          const label = citationLabel(citation.path);
          return (
            <li key={`${citation.path}#${citation.line ?? ''}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[var(--text-tertiary)] shrink-0" aria-hidden />
                <span
                  className={
                    label.mono
                      ? 'font-mono text-xs text-[var(--text-primary)]'
                      : 'text-sm font-medium text-[var(--text-primary)]'
                  }
                  title={citation.path}
                >
                  {label.text}
                  {citation.line != null && (
                    <span className="text-[var(--text-tertiary)]">, line {citation.line}</span>
                  )}
                </span>
              </div>
              {citation.snippet && (
                <p
                  className="mt-0.5 text-xs text-[var(--text-tertiary)] truncate"
                  title={citation.snippet}
                >
                  {citation.snippet}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
